<script setup>
import { toRef, ref, computed, onMounted } from 'vue';
import { useRsvpStore } from '@/stores/rsvp/rsvp.store';
import { useStateStore } from '@/stores/state.store';

const stateStore = useStateStore();
const rsvpStore = useRsvpStore();

const textFont =
  rsvpStore.clientData.common.textFont &&
  rsvpStore.clientData.common.textFont.trim() !== ''
    ? rsvpStore.clientData.common.textFont
    : 'Alegreya';

const mainColor = toRef(() => rsvpStore.getColors.mainColor);

const day = stateStore.getCommonData.eventDate;
const hour = /^([01]?\d|2[0-3]):[0-5]\d$/.test(rsvpStore.getCommonData.hour)
  ? rsvpStore.getCommonData.hour
  : '00:00';

const targetDateTime = new Date(`${day}T${hour}`);

const currentTime = ref(new Date().getTime());
const targetTime = ref(targetDateTime.getTime());

const timeDifference = computed(() => {
  const diff = targetTime.value - currentTime.value;
  return Math.max(0, diff);
});

const seconds = computed(() => {
  return String(Math.floor((timeDifference.value / 1000) % 60)).padStart(
    2,
    '0',
  );
});
const minutes = computed(() => {
  return String(Math.floor((timeDifference.value / 1000 / 60) % 60)).padStart(
    2,
    '0',
  );
});
const hours = computed(() => {
  return String(
    Math.floor((timeDifference.value / 1000 / 60 / 60) % 24),
  ).padStart(2, '0');
});
const days = computed(() => {
  return Math.floor(timeDifference.value / 1000 / 60 / 60 / 24);
});

onMounted(() => {
  setInterval(() => {
    currentTime.value = new Date().getTime();
  }, 1000);
});
</script>

<template>
  <div class="backgorund">
    <div class="clock">
      <h2 class="core-default-h2 h2-clock">
        {{ $t('appCore.apps.rsvp.components.clockWidget.title') }}
      </h2>
      <div class="timer">
        <div class="timer-element">
          <p class="number">
            {{ days }}
          </p>
          <p class="text">
            {{ $t('appCore.apps.rsvp.components.clockWidget.clock.days') }}
          </p>
        </div>
        <div class="timer-element">
          <p class="number">
            {{ hours }}
          </p>
          <p class="text">
            {{ $t('appCore.apps.rsvp.components.clockWidget.clock.hours') }}
          </p>
        </div>
        <div class="timer-element">
          <p class="number">
            {{ minutes }}
          </p>
          <p class="text">
            {{ $t('appCore.apps.rsvp.components.clockWidget.clock.minutes') }}
          </p>
        </div>
        <div class="timer-element">
          <p class="number">
            {{ seconds }}
          </p>
          <p class="text">
            {{ $t('appCore.apps.rsvp.components.clockWidget.clock.seconds') }}
          </p>
        </div>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
@import '@/assets/styleConstans';

.clock {
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 35px;
}

.h2-clock {
  font-size: 2.2em;
  line-height: normal;
  font-weight: 700;
  font-family: Raleway, sans-serif;
  text-align: center;

  @media screen and (max-width: $media-phone) {
    font-size: 1.9em;
  }
}

.timer {
  font-size: 2em;
  line-height: 100%;
  font-family: v-bind(textFont), serif;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-wrap: wrap;
  gap: 8px 10px;

  @media screen and (max-width: $media-phone) {
    font-size: 1.65em;
  }

  .timer-element {
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    width: 80px;

    @media screen and (max-width: $media-phone) {
      width: 67px;
    }

    .number {
      font-variant-numeric: lining-nums tabular-nums;
      font-family: Alegreya, serif;
      font-size: 60px;
      font-style: normal;
      font-weight: 500;
      line-height: normal;
      letter-spacing: 2.6px;

      @media screen and (max-width: $media-phone) {
        font-size: 47px;
      }
    }

    .text {
      white-space: nowrap;
      font-family: Lato, sans-serif;
      font-size: 0.7em;
      font-style: normal;
      font-weight: 500;
      line-height: normal;
      letter-spacing: 0.7px;

      @media screen and (max-width: $media-phone) {
        font-size: 0.6em;
      }
    }
  }
}

.backgorund {
  position: relative;
  width: 100%;
  color: v-bind(mainColor);
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
}
</style>
